import React from 'react';

export type QuickActionType =
  | 'summarize'
  | 'transcribe'
  | 'study_notes'
  | 'action_items'
  | 'translate';

interface QuickAction {
  type: QuickActionType;
  label: string;
  icon: string;
  prompt: string;
  description: string;
}

// Define available quick actions for audio/video files
const AUDIO_VIDEO_ACTIONS: QuickAction[] = [
  {
    type: 'summarize',
    label: 'Summarize',
    icon: '📝',
    prompt: 'Please summarize the key points from this audio/video content. Provide a concise overview with main topics discussed.',
    description: 'Get a quick summary of the content',
  },
  {
    type: 'transcribe',
    label: 'Transcribe',
    icon: '📄',
    prompt: 'Please transcribe this audio/video content. Provide the full text transcription with speaker labels if multiple speakers are detected.',
    description: 'Convert speech to text',
  },
  {
    type: 'study_notes',
    label: 'Study Notes',
    icon: '📚',
    prompt: 'Create study notes from this audio/video content. Include: 1) Key concepts with definitions, 2) Important facts to remember, 3) A brief quiz with 3-5 questions to test understanding.',
    description: 'Generate study materials',
  },
  {
    type: 'action_items',
    label: 'Action Items',
    icon: '✅',
    prompt: 'Extract all action items, tasks, and follow-ups mentioned in this audio/video content. List them with any assigned owners or deadlines if mentioned.',
    description: 'Extract tasks and to-dos',
  },
  {
    type: 'translate',
    label: 'Translate',
    icon: '🌐',
    prompt: 'First transcribe, then translate this audio/video content to English. If the content is already in English, translate to Vietnamese.',
    description: 'Transcribe and translate',
  },
];

// Define actions specific to PDF/documents
const DOCUMENT_ACTIONS: QuickAction[] = [
  {
    type: 'summarize',
    label: 'Summarize',
    icon: '📝',
    prompt: 'Please summarize this document. Provide: 1) A brief executive summary (2-3 sentences), 2) Key points organized by topic, 3) Any important conclusions or recommendations.',
    description: 'Get a quick summary',
  },
  {
    type: 'study_notes',
    label: 'Study Notes',
    icon: '📚',
    prompt: 'Create comprehensive study notes from this document. Include: 1) Main concepts with explanations, 2) Important definitions and terms, 3) Key takeaways, 4) A quiz with 5 questions to test understanding.',
    description: 'Generate study materials',
  },
  {
    type: 'action_items',
    label: 'Key Points',
    icon: '💡',
    prompt: 'Extract the most important information from this document. List: 1) Critical facts and figures, 2) Key decisions or conclusions, 3) Any action items or next steps mentioned.',
    description: 'Extract key information',
  },
];

interface MediaQuickActionsProps {
  mediaType: 'audio' | 'video' | 'pdf' | 'document';
  fileName: string;
  onSelectAction: (action: QuickAction) => void;
  disabled?: boolean;
}

const MediaQuickActions: React.FC<MediaQuickActionsProps> = ({
  mediaType,
  fileName,
  onSelectAction,
  disabled = false,
}) => {
  const actions = mediaType === 'pdf' || mediaType === 'document'
    ? DOCUMENT_ACTIONS
    : AUDIO_VIDEO_ACTIONS;

  const getMediaIcon = () => {
    switch (mediaType) {
      case 'audio':
        return '🎵';
      case 'video':
        return '🎬';
      case 'pdf':
        return '📕';
      default:
        return '📄';
    }
  };

  return (
    <div className="media-quick-actions">
      <div className="quick-actions-header">
        <span className="media-icon">{getMediaIcon()}</span>
        <span className="media-filename" title={fileName}>
          {fileName.length > 30 ? fileName.substring(0, 27) + '...' : fileName}
        </span>
      </div>
      <div className="quick-actions-label">Quick Actions:</div>
      <div className="quick-actions-buttons">
        {actions.map((action) => (
          <button
            key={action.type}
            className="quick-action-btn"
            onClick={() => onSelectAction(action)}
            disabled={disabled}
            title={action.description}
          >
            <span className="action-icon">{action.icon}</span>
            <span className="action-label">{action.label}</span>
          </button>
        ))}
      </div>
      <style>{`
        .media-quick-actions {
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 12px;
          padding: 12px 16px;
          margin-top: 8px;
        }

        .quick-actions-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .media-icon {
          font-size: 20px;
        }

        .media-filename {
          color: #e5e7eb;
          font-size: 14px;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .quick-actions-label {
          color: #9ca3af;
          font-size: 12px;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .quick-actions-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .quick-action-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 20px;
          color: #e5e7eb;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .quick-action-btn:hover:not(:disabled) {
          background: rgba(99, 102, 241, 0.3);
          border-color: rgba(99, 102, 241, 0.5);
          transform: translateY(-1px);
        }

        .quick-action-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .quick-action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .action-icon {
          font-size: 14px;
        }

        .action-label {
          font-weight: 500;
        }

        @media (max-width: 640px) {
          .quick-actions-buttons {
            flex-direction: column;
          }

          .quick-action-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export { MediaQuickActions, AUDIO_VIDEO_ACTIONS, DOCUMENT_ACTIONS };
export type { QuickAction };
